import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import useBarlow from "@/components/useBarlow";

const TOTAL = 23;
const slideSrc = (i) => (i === 23 ? "/treinamentos/hospitalar/slide-23.png" : `/treinamentos/hospitalar/slide-${String(i).padStart(2,"0")}.jpg`);
const BARLOW = "'Barlow', sans-serif";

// TRN-2: fotos padrão de produtos.
const PRODUTO_IMG = {
  clormix: "/treinamentos/hospitalar/produto-clormix.jpg",
  wyn: "/treinamentos/hospitalar/produto-wyn.jpg",
  gala: "/treinamentos/hospitalar/produto-gala.jpg",
};

// Fallback: ícone genérico de frasco (SVG, #008BB0 sobre #E6F2F8).
function FrascoIcon() {
  return (
    <svg viewBox="0 0 64 72" width="60%" height="60%" style={{ display: "block" }}>
      <rect x="26" y="4" width="12" height="10" rx="2" fill="#008BB0" />
      <path d="M20 16 h24 v10 l6 10 v26 a8 8 0 0 1 -8 8 h-20 a8 8 0 0 1 -8 -8 v-26 l6 -10 z" fill="#008BB0" />
      <rect x="22" y="40" width="20" height="18" rx="2" fill="#fff" opacity="0.4" />
    </svg>
  );
}

// TRN-2: linha da assinatura no slide 23 (ícone #99D1DF + texto branco).
function AssLinha({ top, scale, iconPath, text, testid }) {
  const px = (v) => `${Math.max(10, v * scale)}px`;
  return (
    <div data-testid={testid} style={{ position: "absolute", left: "4.9%", top, display: "flex", alignItems: "center", gap: px(14), fontFamily: BARLOW, color: "#fff", fontSize: px(26), lineHeight: 1 }}>
      <svg viewBox="0 0 24 24" width={px(28)} height={px(28)} style={{ flexShrink: 0 }}>
        <path d={iconPath} fill="none" stroke="#99D1DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>{text}</span>
    </div>
  );
}

export default function TreinamentoHospitalar() {
  useBarlow();
  const navigate = useNavigate();
  const location = useLocation();
  const { apid } = useParams();
  const search = new URLSearchParams(location.search);
  const stateToken = location.state?.token || search.get("token");
  const stateCliente = location.state?.clienteNome || search.get("cli") || "";
  const [idx, setIdx] = useState(1);
  const [qrUrl, setQrUrl] = useState("");
  const [portraitWarn, setPortraitWarn] = useState(false);
  const [ignoreOrient, setIgnoreOrient] = useState(false);
  // TRN-2: aplicação com apresentador + produtos (snapshot do backend).
  const [aplicacao, setAplicacao] = useState(location.state?.apresentacao || null);
  const [slideW, setSlideW] = useState(1920);
  const touchStartX = useRef(null);
  const slideRef = useRef(null);
  useEffect(() => {
    if (!slideRef.current) return;
    const el = slideRef.current;
    const update = () => setSlideW(el.offsetWidth || 1920);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [idx]);

  // TRN-2: busca aplicação para ter apresentador + produtos (snapshot persistido).
  useEffect(() => {
    if (!apid || aplicacao) return;
    (async () => {
      try {
        const r = await api.get(`/canal-cliente/treinamentos-aplicados/${apid}`);
        setAplicacao(r.data || null);
      } catch (e) { /* mantém sem overlay se falhar */ }
    })();
  }, [apid, aplicacao]);

  // Detecta orientação (celular em pé)
  useEffect(() => {
    const checkOrient = () => {
      const isPhone = window.innerWidth < 768;
      const isPortrait = window.innerHeight > window.innerWidth;
      setPortraitWarn(isPhone && isPortrait);
    };
    checkOrient();
    window.addEventListener("resize", checkOrient);
    window.addEventListener("orientationchange", checkOrient);
    return () => {
      window.removeEventListener("resize", checkOrient);
      window.removeEventListener("orientationchange", checkOrient);
    };
  }, []);

  // Pré-carrega QR real quando chegar no slide 23
  useEffect(() => {
    if (idx !== 23 || qrUrl || !stateToken) return;
    const url = `${window.location.origin}/q/${stateToken}`;
    (async () => {
      try {
        const r = await api.get("/canal-cliente/qr.png", { params: { token: stateToken, url, size: 1024 }, responseType: "blob" });
        setQrUrl(URL.createObjectURL(r.data));
      } catch (e) { /* ignora: slide ainda exibe o fundo */ }
    })();
  }, [idx, qrUrl, stateToken]);

  // Teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); setIdx((i) => Math.min(TOTAL, i + 1)); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); setIdx((i) => Math.max(1, i - 1)); }
      else if (e.key === "Escape") { sair(); }
      else if (e.key === "f" || e.key === "F") { toggleFullscreen(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    const d = document;
    if (!d.fullscreenElement) d.documentElement.requestFullscreen?.();
    else d.exitFullscreen?.();
  };

  const sair = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    navigate("/treinamentos");
  };

  const avancar = () => setIdx((i) => Math.min(TOTAL, i + 1));
  const voltar = () => setIdx((i) => Math.max(1, i - 1));

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 50) voltar();
    else if (dx < -50) avancar();
  };

  if (portraitWarn && !ignoreOrient) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#003E62", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", fontFamily: BARLOW, zIndex: 2000 }} data-testid="trn-portrait-warning">
        <div style={{ fontSize: 72, marginBottom: 18 }}>↻</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Gire o celular para ver melhor</div>
        <div style={{ color: "#D7E6EE", fontSize: 14, marginTop: 8, maxWidth: 320 }}>A apresentação fica em tela cheia quando o celular está deitado.</div>
        <button onClick={() => setIgnoreOrient(true)} data-testid="trn-continuar-portrait"
          style={{ marginTop: 24, height: 48, padding: "0 22px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.4)", background: "transparent", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: BARLOW, cursor: "pointer" }}>
          Continuar assim
        </button>
      </div>
    );
  }

  const pct = (idx / TOTAL) * 100;

  // Escala de fontes relativa à largura real do slide (base 1920 px).
  const scale = slideW / 1920;
  const px = (v) => `${Math.max(10, v * scale)}px`;

  // TRN-2: assinatura do apresentador (snapshot do backend ou user atual).
  const apre = aplicacao?.apresentador || {};
  // TRN-2.1: formata telefone brasileiro (só se vier com dígitos puros 10/11).
  const formatTel = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim();
    const d = s.replace(/\D/g, "");
    if (d.length === 11) return `+55 (${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `+55 (${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return s; // já formatado ou tamanho inesperado
  };
  // TRN-2.1: cargo = snapshot não vazio OU rótulo do papel vindo do state OU fallback.
  const cargoRaw = (apre.cargo || "").trim() || (location.state?.cargoLabel || "").trim();
  const assinatura = {
    nome: apre.nome || aplicacao?.aplicado_por_nome || "",
    cargo: cargoRaw ? `${cargoRaw} · MPires` : "Equipe Comercial · MPires",
    email: apre.email || "",
    telefone: formatTel(apre.telefone || "") || "+55 (31) 3412-4040",
    endereco: "Av. Américo Vespúcio, 1525 · Belo Horizonte, MG",
    site: "www.mpires.com.br",
  };

  // TRN-2: produtos aplicados (snapshot do backend, fallback para padrão).
  const produtos = Array.isArray(aplicacao?.produtos) && aplicacao.produtos.length
    ? aplicacao.produtos.slice(0, 4)
    : [
        { key: "clormix", nome: "Clormix", tipo: "Limpeza e desinfecção clorada", indicacao: "Superfícies e equipamentos" },
        { key: "wyn", nome: "Wyn Peroxy HC", tipo: "Desinfetante à base de peróxido", indicacao: "Áreas críticas e semicríticas" },
        { key: "gala", nome: "Gala Plus", tipo: "Detergente alcalino para pisos", indicacao: "Pisos e áreas extensas" },
      ];
  const nProd = produtos.length;
  const cardW = nProd >= 4 ? 20.6 : 28.3;
  const gap = nProd >= 4 ? 1.4 : 2.65;
  const totalW = cardW * nProd + gap * (nProd - 1);
  const leftStart = (100 - totalW) / 2;
  const smaller = nProd >= 4;
  const trunc = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : (s || ""));

  return (
    <div style={{ position: "fixed", inset: 0, background: "#003E62", color: "#fff", fontFamily: BARLOW, overflow: "hidden" }} data-testid={`trn-slide-${idx}`}
         onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Slide centralizado 16:9 */}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div ref={slideRef} style={{ position: "relative", width: "min(100vw, calc(100vh * 16/9))", height: "min(100vh, calc(100vw * 9/16))" }}>
          <img src={slideSrc(idx)} alt={`Slide ${idx}`} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />

          {/* TRN-2: Slide 19 — cards de produtos editáveis por cima do fundo. */}
          {idx === 19 && (
            <div data-testid="trn-produtos-wrap" style={{ position: "absolute", left: 0, top: "25.3%", width: "100%", height: "55.3%", display: "flex", justifyContent: "flex-start", alignItems: "stretch", gap: `${gap}%`, paddingLeft: `${leftStart}%`, paddingRight: `${leftStart}%`, boxSizing: "border-box" }}>
              {produtos.map((p, i) => {
                const img = PRODUTO_IMG[p.key];
                return (
                  <div key={i} data-testid={`trn-prod-${i}`} style={{ width: `${cardW}%`, background: "#fff", borderRadius: px(20), boxShadow: "0 6px 24px rgba(0,30,55,0.10)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <div style={{ height: "47%", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", padding: px(10) }}>
                      {img ? (
                        <img src={img} alt={p.nome} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                      ) : (
                        <div style={{ width: "60%", height: "60%", background: "#E6F2F8", borderRadius: px(14), display: "flex", alignItems: "center", justifyContent: "center" }}><FrascoIcon /></div>
                      )}
                    </div>
                    <div style={{ padding: `${px(smaller ? 8 : 10)} ${px(smaller ? 14 : 18)} ${px(smaller ? 14 : 18)}`, display: "flex", flexDirection: "column", gap: px(6), flex: 1 }}>
                      <div style={{ color: "#003E62", fontFamily: "'Inter','Barlow',sans-serif", fontWeight: 700, fontSize: px(smaller ? 34 : 42), lineHeight: 1.1 }}>{trunc(p.nome, 24)}</div>
                      <div style={{ color: "#008BB0", fontWeight: 700, fontSize: px(smaller ? 20 : 26), lineHeight: 1.2 }}>{trunc(p.tipo, 40)}</div>
                      <div style={{ marginTop: "auto", background: "#E6F2F8", borderRadius: px(14), padding: `${px(8)} ${px(12)}`, color: "#003E62", fontSize: px(smaller ? 20 : 26), lineHeight: 1.25 }}>
                        <span style={{ fontWeight: 700 }}>Indicação: </span>
                        <span style={{ fontWeight: 400 }}>{trunc(p.indicacao, 50)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* QR real sobreposto no slide 23 */}
          {idx === 23 && (
            <>
              <div style={{ position: "absolute", left: "71.2%", top: "30.6%", width: "16.9%", aspectRatio: "1 / 1", background: "#fff", borderRadius: 6, padding: "3%", boxSizing: "border-box" }} data-testid="trn-qr-wrap">
                {qrUrl ? (
                  <img src={qrUrl} alt="QR Avaliação" style={{ width: "100%", height: "100%", objectFit: "contain" }} data-testid="trn-qr-img" />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#003E62", fontSize: 12, fontWeight: 700 }}>Gerando QR…</div>
                )}
              </div>
              {/* TRN-2: bloco de assinatura do apresentador (sobre o fundo sem contato). */}
              <div data-testid="trn-assinatura" style={{ position: "absolute", left: "4.9%", right: "40%", top: "57.3%", color: "#fff", fontFamily: BARLOW, lineHeight: 1.1 }}>
                <div data-testid="trn-ass-nome" style={{ fontWeight: 700, fontSize: px(36), color: "#fff" }}>{assinatura.nome}</div>
              </div>
              <div style={{ position: "absolute", left: "4.9%", right: "40%", top: "62.7%", color: "#99D1DF", fontFamily: BARLOW, fontSize: px(26), fontWeight: 500 }}>{assinatura.cargo}</div>
              {assinatura.email && (
                <AssLinha top="70.0%" scale={scale} iconPath="M3 7l9 6 9-6M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M3 7l2-2h14l2 2" text={assinatura.email} testid="trn-ass-email" />
              )}
              <AssLinha top="75.6%" scale={scale} iconPath="M4 6c0-1 .8-2 2-2h2l2 4-2 1a9 9 0 005 5l1-2 4 2v2c0 1-1 2-2 2-8 0-14-6-14-12z" text={assinatura.telefone} testid="trn-ass-tel" />
              <AssLinha top="81.2%" scale={scale} iconPath="M12 2a7 7 0 00-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 00-7-7zm0 10a3 3 0 110-6 3 3 0 010 6z" text={assinatura.endereco} testid="trn-ass-end" />
              <AssLinha top="86.8%" scale={scale} iconPath="M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h20M12 2a16 16 0 010 20M12 2a16 16 0 000 20" text={assinatura.site} testid="trn-ass-site" />
            </>
          )}
        </div>
      </div>

      {/* Zonas de clique (não cobrem conteúdo visualmente) */}
      <div onClick={voltar} data-testid="trn-click-left" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "20%", cursor: idx > 1 ? "w-resize" : "default" }} />
      <div onClick={avancar} data-testid="trn-click-right" style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "20%", cursor: idx < TOTAL ? "e-resize" : "default" }} />

      {/* Botões ‹ › */}
      <button onClick={voltar} disabled={idx === 1} data-testid="trn-btn-prev" aria-label="Voltar"
        style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 44, height: 44, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.25)", color: "#fff", fontSize: 22, fontWeight: 700, cursor: idx === 1 ? "default" : "pointer", opacity: idx === 1 ? 0.3 : 1 }}>‹</button>
      <button onClick={avancar} disabled={idx === TOTAL} data-testid="trn-btn-next" aria-label="Avançar"
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 44, height: 44, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.25)", color: "#fff", fontSize: 22, fontWeight: 700, cursor: idx === TOTAL ? "default" : "pointer", opacity: idx === TOTAL ? 0.3 : 1 }}>›</button>

      {/* Botão X sair */}
      <button onClick={sair} data-testid="trn-btn-sair" aria-label="Sair"
        style={{ position: "absolute", top: 10, right: 10, width: 36, height: 36, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.25)", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer" }}>✕</button>

      {/* Contador + barra */}
      <div style={{ position: "absolute", left: 12, bottom: 12, color: "rgba(215,230,238,0.75)", fontSize: 12, fontWeight: 600 }} data-testid="trn-contador">{idx} / {TOTAL}{stateCliente ? ` · ${stateCliente}` : ""}</div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: "rgba(255,255,255,0.08)" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "#99D1DF", transition: "width 240ms ease" }} />
      </div>
    </div>
  );
}
